import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";
import { MdClose, MdWarningAmber } from "react-icons/md";
import { BsCheck } from "react-icons/bs";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { DetailedCitizenScoresIndicatorC2Data } from "@/services/esus/indicator/esf/c2/getDetailedCitizensScoresIndicatorC2";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";
import { getCitizenScoreStatusDescriptionIndicatorC2 } from "@/utils/indicatorsEsf";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC2CitizensScoresTableColumns = {
	citizenName: Column;
	healthTeam: Column;
	microarea: Column;
	firstAttendanceStatus: Column;
	nineAttendancesStatus: Column;
	nineMetricsRegistersStatus: Column;
	twoVisitsStatus: Column;
	vaccinationsStatus: Column;
	score: Column;
};

type IndicatorEsfC2CitizensScoresTableProps = {
	citizensScores: DetailedCitizenScoresIndicatorC2Data[];
	isLoading: boolean;
};

export function getIconByCitizenScoreStatusIndicatorC2(
	citizenScoreStatus: "finished" | "pending" | "lost"
) {
	switch (citizenScoreStatus) {
		case "finished":
			return (
				<BsCheck
					className="text-primary fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC2(citizenScoreStatus)}
				/>
			);
		case "pending":
			return (
				<MdWarningAmber
					className="text-warning fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC2(citizenScoreStatus)}
				/>
			);
		case "lost":
			return (
				<MdClose
					className="text-danger fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC2(citizenScoreStatus)}
				/>
			);
	}
}

export function IndicatorEsfC2CitizensScoresTable({
	citizensScores,
	isLoading,
}: Readonly<IndicatorEsfC2CitizensScoresTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC2CitizensScoresTableColumns>({
		citizenName: { title: "Cidadão", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		firstAttendanceStatus: { title: "1ª Consulta", visible: true },
		nineAttendancesStatus: { title: "Consultas", visible: true },
		nineMetricsRegistersStatus: { title: "Peso/Altura", visible: true },
		twoVisitsStatus: { title: "Visitas", visible: true },
		vaccinationsStatus: { title: "Vacinações", visible: true },
		score: { title: "Pontos", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.firstAttendanceStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.firstAttendanceStatus.title}
							<CardOverlayTrigger
								header="1ª Consulta"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com a 1ª Consulta presencial realizada <br />
									por médico ou enfermeiro até o 30º dia de vida <br />
									(Prática A - 20 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.nineAttendancesStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.nineAttendancesStatus.title}
							<CardOverlayTrigger
								header="Consultas"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com consultas realizadas por médico ou enfermeiro até os
									2 anos de vida (a portaria indica que sejam realizadas 9
									consultas) <br />
									(Prática B - 20 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.nineMetricsRegistersStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.nineMetricsRegistersStatus.title}
							<CardOverlayTrigger
								header="Registros de peso e altura"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com atendimentos individuais ou procedimentos realizados
									até os 2 anos de <br />
									vida que constem informações de peso e altura (a portaria indica
									que sejam <br />
									realizados 9 registros) <br />
									(Prática C - 20 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.twoVisitsStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.twoVisitsStatus.title}
							<CardOverlayTrigger
								header="Visitas"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com visitas realizada por ACS ou TACS com a informação
									de motivo da visita <br />
									preenchida como 'Acompanhamento de recém-nascido' sendo a
									primeira até 30 dias de <br />
									vida e a segunda até 6 meses de vida <br />
									(Prática D - 20 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.vaccinationsStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.vaccinationsStatus.title}
							<CardOverlayTrigger
								header="Vacinações"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com esquema vacinal em dias que tenha sido imunizado
									contra difteria, tétano, <br />
									coqueluche, hepatite B, infecções causadas por Haemophilus
									Influenzae tipo B, <br />
									poliomielite, sarampo, caxumba e rubéola, com todas as doses
									recomendadas <br />
									(Prática E - 20 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.score.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.score.title}{" "}
							<CardOverlayTrigger
								header="Pontuação"
								message="Para cada prática existe uma pontuação específica, onde a soma de todas as práticas totaliza 100 pontos por cidadão"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC2CitizensScoresTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC2CitizensScoresTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC2CitizensScoresTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensScores.length > 0
					? citizensScores.map((citizenScores, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.citizenName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{citizenScores.fullName}</div>
										<div className="text-muted">
											{dayjs(citizenScores.birthdate).format("DD/MM/YYYY")}
										</div>
									</div>
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenScores.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenScores.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenScores.area ?? "FA"}
								</td>
								<td
									className={`px-2 ${
										!columns.firstAttendanceStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC2(
										citizenScores.firstAttendanceStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.nineAttendancesStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC2(
										citizenScores.nineAttendancesStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.nineMetricsRegistersStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC2(
										citizenScores.nineWeightAndHeightMetricsRegistersStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.twoVisitsStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC2(
										citizenScores.twoHomeVisitsStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.vaccinationsStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC2(
										citizenScores.vaccinationsStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.score.visible && "d-none"
									}`}
								>
									{citizenScores.score}
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
